import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { keyframes } from '@emotion/react';

// アニメーション定義
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.02); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.3; }
  50% { transform: scale(1.1) rotate(1deg); opacity: 0.5; }
`;

const VideoGenerationBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Link to="/generate/video" style={{ textDecoration: 'none' }}>
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          maxWidth: '1000px',
          minHeight: { xs: '200px', sm: '230px' },
          maxHeight: { xs: '200px', sm: '230px' },
          margin: '0 auto',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[6],
            '& .floating-icon': {
              animation: `${float} 2s ease-in-out infinite`,
            },
          }
        }}
      >
        {/* 背景装飾エフェクト */}
        <Box
          sx={{
            position: 'absolute',
            top: '-30%',
            right: '-15%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)',
            animation: `${breathe} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-10%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
            animation: `${breathe} 10s ease-in-out infinite reverse`,
          }}
        />

        {/* グリッド線背景 */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />

        {/* メインコンテンツ */}
        <Box
          sx={{
            height: '100%',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 2,
            gap: { xs: 2, sm: 3 }
          }}
        >
          {/* 左側: テキストコンテンツ */}
          <Box sx={{ 
            flex: 1,
            color: 'white',
            maxWidth: { xs: '65%', sm: '70%', md: 'none' }
          }}>
            {/* NEW バッジ */}
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#ff4757',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 'bold',
                mb: 0.5,
                boxShadow: '0 2px 8px rgba(255, 71, 87, 0.3)',
                animation: `${pulse} 3s ease-in-out infinite`,
              }}
            >
              🎉 新規リリース
            </Box>

            {/* メインタイトル */}
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 900,
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                lineHeight: { xs: 1.2, sm: 1.3 },
                color: '#ffffff',
                textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(255,255,255,0.3)',
              }}
            >
              AI動画生成
            </Typography>

            {/* サブタイトルとCTAボタンの横並び配置 */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1,
              gap: 2
            }}>
              {/* サブタイトル */}
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' },
                  opacity: 0.95,
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                テキストから高品質な動画を生成
              </Typography>

              {/* CTAボタン */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'linear-gradient(45deg, #ff6b6b, #ffa500)',
                  border: 'none',
                  borderRadius: 3,
                  padding: { xs: '6px 12px', sm: '8px 16px' },
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255, 107, 107, 0.6)',
                  }
                }}
              >
                <PlayCircleFilledIcon sx={{ mr: 0.5, fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                今すぐ試す
              </Box>
            </Box>

            {/* 機能ハイライト */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  fontWeight: 500,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                高品質AI
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  fontWeight: 500,
                }}
              >
                <VideoCallIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                簡単操作
              </Box>
            </Box>
          </Box>

          {/* 右側: アイコンとビジュアル */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: { xs: '80px', sm: '120px', md: '150px' },
              height: '100%',
            }}
          >
            {/* メインアイコン */}
            <Box
              className="floating-icon"
              sx={{
                position: 'relative',
                mb: 0.5,
              }}
            >
              <PlayCircleFilledIcon
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                  color: '#fff',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                }}
              />
              <AutoAwesomeIcon
                sx={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  fontSize: { xs: '1rem', sm: '1.3rem' },
                  color: '#FFD700',
                  animation: `${pulse} 4s ease-in-out infinite`,
                }}
              />
            </Box>

            {/* 装飾的な小アイコン */}
            <Box sx={{ display: 'flex', gap: 1, opacity: 0.8 }}>
              <VideoCallIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: '#fff' }} />
              <AutoAwesomeIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: '#FFD700' }} />
              <VideoCallIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: '#fff' }} />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Link>
  );
};

export default VideoGenerationBanner;
